import approvalWorkflowEN from './locales/en/approvalWorkflow.json';
import approvalWorkflowPTBR from './locales/pt-BR/approvalWorkflow.json';
import commonEN from './locales/en/common.json';
import errorEN from './locales/en/error.json';
import fieldEN from './locales/en/field.json';
import accessMapEN from './locales/en/accessMap.json';
import chatEN from './locales/en/chat.json';
import contractsEN from './locales/en/contracts.json';
import decisionsEN from './locales/en/decisions.json';
import auditEN from './locales/en/audit.json';
import usersEN from './locales/en/users.json';
import tenantSettingsEN from './locales/en/tenantSettings.json';
import overviewEN from './locales/en/overview.json';
import loginEN from './locales/en/login.json';
import conversationEN from './locales/en/conversation.json';
import runtimeStatusEN from './locales/en/runtimeStatus.json';
import shellEN from './locales/en/shell.json';
import identitiesEN from './locales/en/identities.json';
import sitePostureEN from './locales/en/sitePosture.json';
import approvalsEN from './locales/en/approvals.json';
import resourcesEN from './locales/en/resources.json';
import simulatorEN from './locales/en/simulator.json';
import identityAccessEN from './locales/en/identityAccess.json';
import humanAccessEN from './locales/en/humanAccess.json';
import infrastructureEN from './locales/en/infrastructure.json';
import sitesEN from './locales/en/sites.json';
import docsEN from './locales/en/docs.json';
import commonPTBR from './locales/pt-BR/common.json';
import errorPTBR from './locales/pt-BR/error.json';
import fieldPTBR from './locales/pt-BR/field.json';
import accessMapPTBR from './locales/pt-BR/accessMap.json';
import chatPTBR from './locales/pt-BR/chat.json';
import contractsPTBR from './locales/pt-BR/contracts.json';
import decisionsPTBR from './locales/pt-BR/decisions.json';
import auditPTBR from './locales/pt-BR/audit.json';
import usersPTBR from './locales/pt-BR/users.json';
import tenantSettingsPTBR from './locales/pt-BR/tenantSettings.json';
import overviewPTBR from './locales/pt-BR/overview.json';
import loginPTBR from './locales/pt-BR/login.json';
import conversationPTBR from './locales/pt-BR/conversation.json';
import runtimeStatusPTBR from './locales/pt-BR/runtimeStatus.json';
import shellPTBR from './locales/pt-BR/shell.json';
import identitiesPTBR from './locales/pt-BR/identities.json';
import sitePosturePTBR from './locales/pt-BR/sitePosture.json';
import approvalsPTBR from './locales/pt-BR/approvals.json';
import resourcesPTBR from './locales/pt-BR/resources.json';
import simulatorPTBR from './locales/pt-BR/simulator.json';
import identityAccessPTBR from './locales/pt-BR/identityAccess.json';
import humanAccessPTBR from './locales/pt-BR/humanAccess.json';
import infrastructurePTBR from './locales/pt-BR/infrastructure.json';
import sitesPTBR from './locales/pt-BR/sites.json';
import docsPTBR from './locales/pt-BR/docs.json';

/**
 * Esqueleto de i18n (B1b). O que ele é e o que ele **não** é:
 *
 *  - é o catálogo por locale, a resolução por chave e a cascata de fallback;
 *  - **não** é a extração das 914 strings de tela — isso é da Onda C, por tela,
 *    e é justamente por isso que os namespaces das telas já nascem aqui vazios:
 *    para dois agentes daquela onda nunca editarem o mesmo arquivo.
 *
 * Sem dependência nova: plural e data saem do `Intl` nativo quando a hora chegar.
 *
 * **Onda C · Lote 2a — a hora chegou.** Os namespaces de tela entram um por
 * módulo, na ordem da frente de extração (`identities`, `resources`, …); o
 * plural passa a sair do `Intl.PluralRules` (`tn()`); o locale ganhou seletor na
 * topbar, persistência e assinantes. Nada disso muda a resolução: `t()` é o
 * mesmo de 02/09.
 */

/** Locales que o produto conhece. `pt-BR` é a base e nunca falta. */
export const LOCALES = ['pt-BR', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

export const BASE_LOCALE: Locale = 'pt-BR';

/**
 * Namespaces. `error` e `field` são a taxonomia que vem do contrato; `common`
 * são rótulos repetidos. Os de tela entram na Onda C.
 *
 * **Por que `error.*` e `field.*` têm formatos de chave diferentes**, e isso é
 * de propósito: a chave de `error` é o `code` do envelope — vocabulário do
 * contrato, que não é nosso para renomear (o do middleware menos ainda), então
 * mapeá-lo para camelCase criaria uma segunda tabela para desalinhar. Já as
 * chaves de `field` nós inventamos, e seguem a convenção do A4: `camelCase`.
 */
const CATALOG: Record<Locale, Record<string, Record<string, string>>> = {
  'pt-BR': {
    error: errorPTBR,
    field: fieldPTBR,
    common: commonPTBR,
    // Namespaces DE TELA: um arquivo por modulo (regra do cabecalho: duas frentes
    // nunca editam o mesmo arquivo). `sitePosture` veio com a P5; `identities` e o
    // primeiro da extracao por tela (i18n-telas).
    sitePosture: sitePosturePTBR,
    identities: identitiesPTBR,
    resources: resourcesPTBR,
    approvals: approvalsPTBR,
    shell: shellPTBR,
    contracts: contractsPTBR,
    decisions: decisionsPTBR,
    runtimeStatus: runtimeStatusPTBR,
    // Bloco 2 (p5-posturas): as telas que estavam «por último».
    simulator: simulatorPTBR,
    audit: auditPTBR,
    users: usersPTBR,
    identityAccess: identityAccessPTBR,
    humanAccess: humanAccessPTBR,
    // `infrastructure`, e nao `infra`: `infra.*` e prefixo de permissao
    // (`infra.salvar` em useCan) — mesmo caso do `runtime` da T15.
    infrastructure: infrastructurePTBR,
    // `sites`, no PLURAL, e a escolha e obrigatoria: `site.*` colide com a acao de
    // trilha `site.create` de `Audit/AuditModel.ts > ACTIONS_KNOWN`, e a varredura
    // do `screenCatalog.test.ts` — larga de proposito — nao distingue as duas
    // familias. Mesmo caso do `runtime` (T15) e do `tenant` (T21).
    sites: sitesPTBR,
    tenantSettings: tenantSettingsPTBR,
    approvalWorkflow: approvalWorkflowPTBR,
    overview: overviewPTBR,
    login: loginPTBR,
    conversation: conversationPTBR,
    accessMap: accessMapPTBR,
    chat: chatPTBR,
    // `docs`: a moldura de `/docs` (cabeçalho, navegação, rodapé). O Markdown do
    // conteúdo NÃO passa por aqui — ele é texto do produto, com um arquivo por idioma.
    docs: docsPTBR,
  },
  en: {
    error: errorEN,
    field: fieldEN,
    common: commonEN,
    sitePosture: sitePostureEN,
    identities: identitiesEN,
    resources: resourcesEN,
    approvals: approvalsEN,
    shell: shellEN,
    contracts: contractsEN,
    decisions: decisionsEN,
    runtimeStatus: runtimeStatusEN,
    simulator: simulatorEN,
    audit: auditEN,
    users: usersEN,
    identityAccess: identityAccessEN,
    humanAccess: humanAccessEN,
    infrastructure: infrastructureEN,
    sites: sitesEN,
    tenantSettings: tenantSettingsEN,
    approvalWorkflow: approvalWorkflowEN,
    overview: overviewEN,
    login: loginEN,
    conversation: conversationEN,
    accessMap: accessMapEN,
    chat: chatEN,
    docs: docsEN,
  },
};

/** Chave do `localStorage` com a preferência de idioma (ver `loadLocale`). */
const STORAGE_KEY = 'aim.locale';

function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/**
 * Preferência gravada, ou o locale base.
 *
 * **Por que `localStorage` e não o usuário:** a API não tem campo de preferência
 * de idioma — nem `Usuario` no contrato nem a sessão o expõem (conferido em
 * 09/09). Quando tiver, o servidor passa a ser a fonte e o armazenamento local
 * vira só cache de primeira pintura; quem muda é **esta função**, e nenhuma tela.
 */
function loadLocale(): Locale {
  try {
    const saved = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (isLocale(saved)) return saved;
  } catch {
    // Storage bloqueado (modo privado, iframe sem permissão): o base resolve.
  }
  return BASE_LOCALE;
}

let active: Locale = loadLocale();

/** Assinantes da troca de locale — é por aqui que o React sabe que tem de repintar. */
const listeners = new Set<() => void>();

/** Inscreve um ouvinte na troca de locale; devolve o cancelamento. */
export function subscribeLocale(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener) as unknown as void;
}

/** Locale corrente. */
export function locale(): Locale {
  return active;
}

/**
 * Troca o locale, persiste e avisa os assinantes.
 *
 * A persistência é best-effort de propósito: com `localStorage` bloqueado a
 * troca ainda vale para a sessão — perder a preferência é menos ruim que não
 * trocar de idioma.
 */
export function setLocale(next: Locale): void {
  if (next === active) return;
  active = next;
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, next);
  } catch {
    // Sem persistência; a sessão corrente já mudou.
  }
  for (const listener of [...listeners]) listener();
}

const NUMERO = new Map<Locale, Intl.NumberFormat>();

/**
 * Número no formato do idioma ativo — separador de milhar incluído.
 *
 * Exportado para quem precisa do número fora de uma frase do catálogo (um
 * `<td>` com a contagem, por exemplo). Dentro de uma frase não é preciso
 * chamar: a interpolação já formata todo `{marcador}` numérico.
 *
 * O `Intl.NumberFormat` é caro de construir; um por idioma, guardado.
 */
export function numero(valor: number): string {
  let fmt = NUMERO.get(active);
  if (!fmt) {
    fmt = new Intl.NumberFormat(active);
    NUMERO.set(active, fmt);
  }
  return fmt.format(valor);
}

/** Interpolação **nomeada** — a ordem das palavras muda entre idiomas. */
export type Params = Record<string, string | number | undefined>;

function interpolate(text: string, params?: Params): string {
  if (!params) return text;
  return text.replace(/\{(\w+)\}/g, (raw, name: string) => {
    const value = params[name];
    if (value === undefined) return raw;
    // Número sai no formato do idioma — «1.200» em pt-BR, «1,200» em en. Fica
    // aqui, e não no `tn()`, porque contagem também chega por `t()`: o «{n} no
    // filtro» da auditoria e o «{done} de {total}» dos primeiros passos são
    // `t()` puro, e as duas grafias apareceriam lado a lado na mesma tela.
    // Abaixo de mil o `Intl` não muda nada.
    return typeof value === 'number' ? numero(value) : String(value);
  });
}

function lookup(loc: Locale, key: string): string | undefined {
  const dot = key.indexOf('.');
  if (dot < 0) return undefined;
  const ns = key.slice(0, dot);
  const rest = key.slice(dot + 1);
  return CATALOG[loc]?.[ns]?.[rest];
}

/**
 * Texto de uma chave. Cascata: locale pedido → `pt-BR` → **a própria chave**.
 *
 * A chave crua na tela é feia de propósito: é bug de catálogo, e tem de doer na
 * revisão em vez de virar string vazia que ninguém vê. O teste de completude
 * (`i18n.test.ts`) impede que ela chegue lá.
 */
export function t(key: string, params?: Params): string {
  const hit =
    lookup(active, key) ?? (active === BASE_LOCALE ? undefined : lookup(BASE_LOCALE, key));
  return interpolate(hit ?? key, params);
}

/** `true` quando a chave existe no locale base — quem decide caminho, não texto. */
export function hasKey(key: string): boolean {
  return lookup(BASE_LOCALE, key) !== undefined;
}

/**
 * Plural com as duas formas que pt-BR e en têm: `<chave>.one` e `<chave>.other`.
 *
 * Quem escolhe a forma é o `Intl.PluralRules` do locale **ativo**, não um
 * `n === 1` escrito à mão: a regra do inglês e a do português divergem, e
 * divergem justamente nos números que ninguém testa. `{n}` já vai interpolado;
 * outros marcadores passam em `params`.
 *
 * **Zero é plural, e isso é decisão nossa contra o CLDR** (09/09). O CLDR põe o
 * `0` na categoria `one` do português (`Intl.PluralRules('pt-BR').select(0) ===
 * 'one'`), o que renderiza «0 regra». Duas razões para não seguir:
 *
 *  - a tela **já dizia** «0 regras» antes desta extração — todo call site era
 *    `n === 1 ? singular : plural` —, e mudar isso seria reescrever copy, que é
 *    justamente o que esta frente não faz;
 *  - em português de uso, zero pede plural.
 *
 * Uma chave pode discordar declarando `<chave>.zero` — é por onde entra
 * «nenhuma regra», que é o que o glossário pede para estado vazio. Em inglês o
 * CLDR já põe `0` em `other`, então nada muda lá.
 *
 * Cai em `.other` quando a forma escolhida não existe — mesmo princípio da
 * cascata de `t()`: uma frase do idioma certo antes da chave crua.
 */
export function tn(key: string, n: number, params?: Params): string {
  // O `n` vai cru: quem o formata pelo idioma é a interpolação (`{n}` → «1.200»
  // em pt-BR). A **forma** do plural continua sendo escolhida pelo número, que
  // é o que o `Intl.PluralRules` sabe ler.
  const withParams: Params = { n, ...params };
  const has = (form: string) =>
    (lookup(active, `${key}.${form}`) ?? lookup(BASE_LOCALE, `${key}.${form}`)) !== undefined;

  if (n === 0) return t(`${key}.${has('zero') ? 'zero' : 'other'}`, withParams);

  const form = new Intl.PluralRules(active).select(n);
  return t(`${key}.${has(form) ? form : 'other'}`, withParams);
}

/** Só para os testes de completude: as chaves de um namespace num locale. */
export function keysOf(loc: Locale, ns: string): string[] {
  return Object.keys(CATALOG[loc]?.[ns] ?? {});
}

/**
 * Namespaces do catálogo. `error`/`field`/`common` são a taxonomia do contrato,
 * herdada da B1b; os de tela entram um por módulo, na ordem da frente.
 */
export const NAMESPACES = [
  'error',
  'field',
  'common',
  'sitePosture',
  'identities',
  'resources',
  'approvals',
  'shell',
  'contracts',
  'accessMap',
  'chat',
  'decisions',
  'runtimeStatus',
  'simulator',
  'audit',
  'users',
  'identityAccess',
  'humanAccess',
  'infrastructure',
  'sites',
  'tenantSettings',
  'approvalWorkflow',
  'overview',
  'login',
  'conversation',
  'docs',
] as const;

/**
 * Namespaces **de tela** — os que o teste de completude cobra em TODOS os
 * locales, não só na base. Um namespace só entra nesta lista quando o `en`
 * dele estiver traduzido; é a lista que separa «traduzido» de «extraído».
 * (`shell`/`contracts` entram quando o p5-posturas fechar o `en` deles.)
 *
 * **Um namespace de tela não pode se chamar como um prefixo de outro
 * vocabulário pontuado do produto.** São dois, e os dois são dado, não texto:
 *
 *  - as **permissões** de `auth/useCan.ts` (`discovery`, `identidade`,
 *    `aprovacao`, `access_control`, `politica`, `runtime`, `resource`, `infra`,
 *    `tenant`, `usuario`);
 *  - as **ações de auditoria** de `Audit/AuditModel.ts › ACTIONS_KNOWN`
 *    (`identidade`, `contrato`, `catalogo`, `resource`, `politica`, `config`,
 *    `tenant`, `usuario`, `discovery`, `admission`, `pdp`, `deployment`,
 *    `sessao`, `role`).
 *
 * As três famílias usam `<prefixo>.<resto>`, e a varredura do
 * `screenCatalog.test.ts` — larga de propósito — não tem como distinguir
 * `runtime.reconciliar` (permissão) de `runtime.page.title` (catálogo).
 *
 * Foi por isso que a T15 virou `runtimeStatus` e não `runtime`; o nome ainda
 * descreve melhor a tela («Status do runtime»). **Atenção para a T21**:
 * `tenant` colide com `tenant.save` e com a permissão `tenant.editar` — use
 * `tenantSettings`. Quem acrescentar um namespace confere as duas listas antes.
 */
export const SCREEN_NAMESPACES = [
  // `common` entrou em 09/09: por ser de todas as telas, ele estava **fora** da
  // cobrança de completude, e foi assim que `common.nothingStored` viveu sem `en`
  // enquanto `errors.ts` o pendurava no fim de mensagem de erro de tela.
  'common',
  'sitePosture',
  'identities',
  'resources',
  'approvals',
  'shell',
  'contracts',
  'accessMap',
  'chat',
  'decisions',
  'runtimeStatus',
  'simulator',
  'audit',
  'users',
  'identityAccess',
  'humanAccess',
  'infrastructure',
  'sites',
  'tenantSettings',
  'approvalWorkflow',
  'overview',
  'login',
  'conversation',
  'docs',
] as const;
